import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { Icon, type IconCode } from "@ui-kit/Icon";
import { PopoverContent } from "@ui-kit/Popover";
import { type ComponentProps, forwardRef, type ReactNode, useId, useRef } from "react";

type ConfirmStatus = "error" | "warning";

export type ConfirmOptions = {
	title: ReactNode;
	description?: ReactNode;
	icon?: IconCode;
	status?: ConfirmStatus;
	confirmText?: string;
	cancelText?: string;
};

type ConfirmPopoverContentProps = ConfirmOptions &
	Pick<ComponentProps<typeof PopoverContent>, "side" | "align"> & {
		isPending?: boolean;
		onConfirm: () => void;
		onCancel: () => void;
	};

const iconClassName: Record<ConfirmStatus, string> = {
	error: "text-status-error",
	warning: "text-status-warning",
};

export const ConfirmPopoverContent = forwardRef<HTMLDivElement, ConfirmPopoverContentProps>((props, ref) => {
	const {
		title,
		description,
		icon,
		status = "error",
		confirmText = t("continue"),
		cancelText = t("cancel"),
		isPending,
		onConfirm,
		onCancel,
		side,
		align = "end",
	} = props;

	const cancelRef = useRef<HTMLButtonElement>(null);
	const titleId = useId();
	const descriptionId = useId();

	return (
		<PopoverContent
			align={align}
			aria-describedby={description ? descriptionId : undefined}
			aria-labelledby={titleId}
			className="flex w-72 flex-col gap-3 p-3"
			onClick={(event) => event.stopPropagation()}
			onFocusOutside={(event) => event.preventDefault()}
			onOpenAutoFocus={(event) => {
				event.preventDefault();
				cancelRef.current?.focus();
			}}
			ref={ref}
			role="alertdialog"
			side={side}
		>
			<div className={cn("grid gap-x-2 gap-y-1", icon && "grid-cols-[1rem_1fr]")}>
				{icon && <Icon className={cn("mt-0.5", iconClassName[status])} icon={icon} size="md" />}
				<div className="text-sm font-semibold text-primary-fg" id={titleId}>
					{title}
				</div>
				{description && (
					<div className={cn("text-xs text-muted", icon && "col-start-2")} id={descriptionId}>
						{description}
					</div>
				)}
			</div>
			<div className="flex justify-end gap-2">
				<Button
					disabled={isPending}
					onClick={onCancel}
					ref={cancelRef}
					size="sm"
					type="button"
					variant="outline"
				>
					{cancelText}
				</Button>
				<Button
					disabled={isPending}
					onClick={onConfirm}
					size="sm"
					status={status}
					type="button"
					variant="primary"
				>
					{confirmText}
				</Button>
			</div>
		</PopoverContent>
	);
});
